import { useState, useEffect, useRef } from "react";
import { X, HelpCircle, Check } from "lucide-react";

/* ── Types ─────────────────────────────────────── */
type Step = 0 | 1 | 2 | 3 | 4;
type SupportType = "compute" | "data" | "financial" | "other";

interface FormData {
  fullName: string;
  affiliation: string;
  hasSponsorship: boolean | null;
  sponsorName: string;
  supportTypes: SupportType[];
  otherSupport: string;
  checkbox1: boolean;
  checkbox2: boolean;
  checkbox3: boolean;
}

const EMPTY_FORM: FormData = {
  fullName: "",
  affiliation: "",
  hasSponsorship: null,
  sponsorName: "",
  supportTypes: [],
  otherSupport: "",
  checkbox1: false,
  checkbox2: false,
  checkbox3: false,
};

const BORDER = "1px solid rgba(59,14,39,0.1)";

/* ── Sub-components ────────────────────────────── */

function Checkbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className="size-[16px] shrink-0 mt-[2px] flex items-center justify-center cursor-pointer"
      style={{
        border: "1.5px solid rgba(59,14,39,0.35)",
        backgroundColor: checked ? "var(--brand-text)" : "transparent",
      }}
    >
      {checked && (
        <Check size={10} color="var(--brand-primary-btn-text)" strokeWidth={3} />
      )}
    </div>
  );
}

function ComplianceCheckbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-[12px]">
      <Checkbox checked={checked} onChange={onChange} />
      <span
        className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] leading-[1.65] cursor-pointer"
        style={{ color: "var(--brand-text)", opacity: 0.8 }}
        onClick={() => onChange(!checked)}
      >
        {children}
      </span>
    </div>
  );
}

function RadioButton({
  selected,
  onSelect,
  label,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-[10px] cursor-pointer" onClick={onSelect}>
      <div
        className="size-[18px] rounded-full flex items-center justify-center shrink-0"
        style={{ border: "1.5px solid rgba(59,14,39,0.35)" }}
      >
        {selected && (
          <div
            className="size-[9px] rounded-full"
            style={{ backgroundColor: "var(--brand-text)" }}
          />
        )}
      </div>
      <span
        className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
        style={{ color: "var(--brand-text)" }}
      >
        {label}
      </span>
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-[44px] px-[14px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] outline-none bg-transparent transition-colors"
      style={{
        border: "1px solid rgba(59,14,39,0.25)",
        color: "var(--brand-text)",
      }}
    />
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label
      className="font-['Helvetica_Neue:Medium',sans-serif] mb-[8px] block text-[14px]"
      style={{ color: "var(--brand-text)" }}
    >
      {children}
    </label>
  );
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="font-['Helvetica_Neue:Light',sans-serif] text-[12px] mt-[6px] leading-[1.55] m-0"
      style={{ color: "var(--brand-text)", opacity: 0.48 }}
    >
      {children}
    </p>
  );
}

function Required() {
  return <span style={{ color: "#b03030" }}> *</span>;
}

function ModuleLabel({ label, title }: { label: string; title: string }) {
  return (
    null
  );
}

/* ── Step Indicator ────────────────────────────── */
const STEPS = [
  { label: "A", name: "Basic Identity" },
  { label: "B", name: "Sponsorship" },
  { label: "C", name: "Compliance" },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div
      className="flex items-center px-[24px] py-[14px] shrink-0"
      style={{ borderBottom: BORDER }}
    >
      {STEPS.map((s, i) => {
        const stepNum = i + 1;
        const isActive = current === stepNum;
        const isDone = current > stepNum;
        return (
          <div key={s.label} className="flex items-center flex-1 min-w-0">
            <div className="flex items-center gap-[8px]">
              <div
                className="size-[22px] rounded-full flex items-center justify-center shrink-0"
                style={{
                  backgroundColor:
                    isDone || isActive ? "var(--brand-text)" : "transparent",
                  border:
                    isDone || isActive
                      ? "none"
                      : "1.5px solid rgba(59,14,39,0.25)",
                }}
              >
                {isDone ? (
                  <Check
                    size={11}
                    color="var(--brand-primary-btn-text)"
                    strokeWidth={2.5}
                  />
                ) : (
                  <span
                    className="font-['Helvetica_Neue:Medium',sans-serif] text-[10px]"
                    style={{
                      color: isActive
                        ? "var(--brand-primary-btn-text)"
                        : "rgba(59,14,39,0.35)",
                    }}
                  >
                    {s.label}
                  </span>
                )}
              </div>
              <span
                className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
                style={{
                  color: "var(--brand-text)",
                  opacity: isActive ? 0.9 : isDone ? 0.55 : 0.3,
                }}
              >
                {s.name}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className="flex-1 h-px mx-[10px]"
                style={{
                  backgroundColor:
                    current > stepNum
                      ? "var(--brand-text)"
                      : "rgba(59,14,39,0.12)",
                  opacity: current > stepNum ? 0.4 : 1,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Main Modal ────────────────────────────────── */
interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function CompetitionRegisterModal({ isOpen, onClose }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [showTooltip, setShowTooltip] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setForm(EMPTY_FORM);
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [step]);

  if (!isOpen) return null;

  /* — Validation — */
  const step1Valid =
    form.fullName.trim().length > 0 && form.affiliation.trim().length > 0;
  const step2Valid =
    form.hasSponsorship !== null &&
    (form.hasSponsorship === false ||
      (form.sponsorName.trim().length > 0 && form.supportTypes.length > 0));
  const step3Valid = form.checkbox1 && form.checkbox2 && form.checkbox3;

  const toggleSupport = (t: SupportType) =>
    setForm((f) => ({
      ...f,
      supportTypes: f.supportTypes.includes(t)
        ? f.supportTypes.filter((x) => x !== t)
        : [...f.supportTypes, t],
    }));

  const headerTitle =
    step === 4
      ? "Registration Complete"
      : "Register for the Challenge";

  const handleSubmit = () => setStep(4);

  const SUPPORT_OPTIONS: { type: SupportType; label: string }[] = [
    {
      type: "compute",
      label: "Compute / Cloud Infrastructure (e.g., GPU clusters, AWS/GCP credits)",
    },
    {
      type: "data",
      label: "Proprietary Data or API Access (e.g., paid LLM APIs, closed datasets)",
    },
    {
      type: "financial",
      label: "Financial Funding or Employer Support (e.g., using company time/resources)",
    },
    { type: "other", label: "Other" },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-[16px] md:p-[24px]">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(0,0,0,0.62)" }}
        onClick={step === 4 ? onClose : undefined}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-[640px] flex flex-col"
        style={{
          backgroundColor: "var(--brand-bg)",
          maxHeight: "min(90vh, 780px)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.22)",
        }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-[24px] shrink-0"
          style={{ height: 64, borderBottom: BORDER }}
        >
          <h2
            className="font-['Helvetica_Neue:Medium',sans-serif] text-[20px] m-0 leading-none"
            style={{ color: "var(--brand-text)" }}
          >
            {headerTitle}
          </h2>
          <button
            onClick={onClose}
            className="cursor-pointer border-none bg-transparent p-0 flex items-center justify-center shrink-0"
          >
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
              <circle cx="15" cy="15" r="15" fill="var(--brand-surface, #F0EDEF)" opacity="0.8" />
              <path
                d="M19.5 10.5L10.5 19.5"
                stroke="var(--brand-text)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M10.5 10.5L19.5 19.5"
                stroke="var(--brand-text)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* ── Step indicator (steps 1–3) ── */}
        {step >= 1 && step <= 3 && <StepIndicator current={step} />}

        {/* ── Scrollable body ── */}
        <div ref={scrollRef} className="overflow-y-auto flex-1">

          {/* ── Step 1: Basic Identity ── */}
          {step === 1 && (
            <div className="px-[24px] py-[24px]">
              <ModuleLabel label="A" title="Basic Identity" />

              <div className="mb-[24px]">
                <FieldLabel>
                  Full Legal Name<Required />
                </FieldLabel>
                <TextInput
                  value={form.fullName}
                  onChange={(v) => setForm((f) => ({ ...f, fullName: v }))}
                  placeholder="e.g., Jane Doe"
                />
                <FieldHint>
                  Please enter your real legal name. This is required for prize distribution and IP verification. Aliases are not permitted.
                </FieldHint>
              </div>

              <div>
                <FieldLabel>
                  Primary Affiliation / Organization<Required />
                </FieldLabel>
                <TextInput
                  value={form.affiliation}
                  onChange={(v) => setForm((f) => ({ ...f, affiliation: v }))}
                  placeholder="e.g., Stanford University, OpenAI, or 'Independent'"
                />
                <FieldHint>
                  The primary academic or corporate institution you represent. If participating independently, please enter "Independent".
                </FieldHint>
              </div>
            </div>
          )}

          {/* ── Step 2: Sponsorship Disclosure ── */}
          {step === 2 && (
            <div className="px-[24px] py-[24px]">
              <ModuleLabel label="B" title="Sponsorship Disclosure" />

              {/* Question + tooltip */}
              <div className="mb-[20px]">
                <div className="flex items-start gap-[8px] mb-[14px]">
                  <FieldLabel>
                    Are you or your team receiving any external material support or sponsorship for this challenge?
                  </FieldLabel>
                  <div
                    className="relative shrink-0 mt-[1px]"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <HelpCircle
                      size={15}
                      style={{ color: "var(--brand-text)", opacity: 0.35, cursor: "default" }}
                    />
                    {showTooltip && (
                      <div
                        className="absolute right-0 top-[22px] z-20 w-[272px] px-[12px] py-[10px]"
                        style={{
                          backgroundColor: "var(--brand-text)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                        }}
                      >
                        <p
                          className="font-['Helvetica_Neue:Light',sans-serif] text-[12px] leading-[1.55] m-0"
                          style={{ color: "var(--brand-primary-btn-text)" }}
                        >
                          "Material support" includes non-public compute resources (GPUs/Cloud credits), closed-source data, proprietary APIs, or dedicated financial funding.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-[28px]">
                  <RadioButton
                    selected={form.hasSponsorship === true}
                    onSelect={() => setForm((f) => ({ ...f, hasSponsorship: true }))}
                    label="Yes"
                  />
                  <RadioButton
                    selected={form.hasSponsorship === false}
                    onSelect={() =>
                      setForm((f) => ({
                        ...f,
                        hasSponsorship: false,
                        sponsorName: "",
                        supportTypes: [],
                        otherSupport: "",
                      }))
                    }
                    label="No"
                  />
                </div>
              </div>

              {/* Conditional fields */}
              {form.hasSponsorship === true && (
                <div
                  className="pt-[20px]"
                  style={{ borderTop: BORDER }}
                >
                  {/* Field 3 */}
                  <div className="mb-[24px]">
                    <FieldLabel>
                      Sponsor / Supporting Entity Name<Required />
                    </FieldLabel>
                    <TextInput
                      value={form.sponsorName}
                      onChange={(v) => setForm((f) => ({ ...f, sponsorName: v }))}
                      placeholder="Enter the name of the company, lab, or fund"
                    />
                  </div>

                  {/* Field 4 */}
                  <div>
                    <FieldLabel>
                      Type of Support Provided<Required />
                    </FieldLabel>
                    <div className="space-y-[14px]">
                      {SUPPORT_OPTIONS.map((opt) => (
                        <div key={opt.type}>
                          <div
                            className="flex items-start gap-[10px] cursor-pointer"
                            onClick={() => toggleSupport(opt.type)}
                          >
                            <Checkbox
                              checked={form.supportTypes.includes(opt.type)}
                              onChange={() => toggleSupport(opt.type)}
                            />
                            <span
                              className="font-['Helvetica_Neue:Regular',sans-serif] leading-[1.55] text-[14px]"
                              style={{ color: "var(--brand-text)", opacity: 0.8 }}
                            >
                              {opt.label}
                            </span>
                          </div>
                          {opt.type === "other" &&
                            form.supportTypes.includes("other") && (
                              <input
                                type="text"
                                value={form.otherSupport}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    otherSupport: e.target.value,
                                  }))
                                }
                                placeholder="Please specify..."
                                className="mt-[8px] ml-[26px] h-[40px] px-[12px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] outline-none bg-transparent"
                                style={{
                                  width: "calc(100% - 26px)",
                                  border: "1px solid rgba(59,14,39,0.25)",
                                  color: "var(--brand-text)",
                                }}
                              />
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Compliance ── */}
          {step === 3 && (
            <div className="px-[24px] py-[24px]">
              <ModuleLabel label="C" title="Compliance &amp; Agreement" />

              <div className="space-y-[20px]">
                <ComplianceCheckbox
                  checked={form.checkbox1}
                  onChange={(v) => setForm((f) => ({ ...f, checkbox1: v }))}
                >
                  I confirm that my provided information is accurate. I understand that each individual may only participate in one (1) team. The use of multiple accounts, undisclosed aliases, or coordinating "sockpuppet" teams will result in the immediate disqualification of all associated participants.
                </ComplianceCheckbox>

                <ComplianceCheckbox
                  checked={form.checkbox2}
                  onChange={(v) => setForm((f) => ({ ...f, checkbox2: v }))}
                >
                  I agree that all submissions (models, code, data) provided to the Challenge and Playground will be used for open research. I grant SAIR Foundation a perpetual, irrevocable license to publish and utilize my submissions. I confirm that neither I nor my disclosed sponsors have any conflicting IP claims that would prevent this open release.
                </ComplianceCheckbox>

                <div className="flex items-start gap-[12px]">
                  <Checkbox
                    checked={form.checkbox3}
                    onChange={(v) => setForm((f) => ({ ...f, checkbox3: v }))}
                  />
                  <span
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] leading-[1.65] cursor-pointer"
                    style={{ color: "var(--brand-text)", opacity: 0.8 }}
                    onClick={() =>
                      setForm((f) => ({ ...f, checkbox3: !f.checkbox3 }))
                    }
                  >
                    I have read and agree to the{" "}
                    <a
                      href="#"
                      onClick={(e) => e.stopPropagation()}
                      className="underline"
                      style={{ color: "var(--brand-text)" }}
                    >
                      Mathematics Distillation Challenge Official Rules
                    </a>{" "}
                    and the{" "}
                    <a
                      href="#"
                      onClick={(e) => e.stopPropagation()}
                      className="underline"
                      style={{ color: "var(--brand-text)" }}
                    >
                      SAIR Foundation Privacy Policy
                    </a>
                    .
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 4: Success ── */}
          {step === 4 && (
            <div className="px-[24px] py-[48px] flex flex-col items-center text-center">
              <div
                className="size-[52px] rounded-full flex items-center justify-center mb-[20px]"
                style={{ backgroundColor: "var(--brand-text)" }}
              >
                <Check
                  size={22}
                  color="var(--brand-primary-btn-text)"
                  strokeWidth={2.5}
                />
              </div>
              <h3
                className="font-['Helvetica_Neue:Medium',sans-serif] text-[20px] m-0 mb-[10px]"
                style={{ color: "var(--brand-text)" }}
              >
                Registration Submitted
              </h3>
              <p
                className="font-['Helvetica_Neue:Light',sans-serif] text-[14px] leading-[1.6] max-w-[400px] m-0"
                style={{ color: "var(--brand-text)", opacity: 0.55 }}
              >
                Your registration has been received. You will be notified by
                email once your application has been reviewed and confirmed.
              </p>

              {/* Read-only summary */}
              <div
                className="mt-[32px] w-full text-left"
                style={{ border: BORDER }}
              >
                <div
                  className="px-[20px] py-[14px]"
                  style={{ borderBottom: BORDER }}
                >
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.8px] uppercase m-0 mb-[3px]"
                    style={{ color: "var(--brand-text)", opacity: 0.38 }}
                  >
                    Full Legal Name
                  </p>
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0"
                    style={{ color: "var(--brand-text)" }}
                  >
                    {form.fullName}
                  </p>
                </div>
                <div
                  className="px-[20px] py-[14px]"
                  style={{ borderBottom: BORDER }}
                >
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.8px] uppercase m-0 mb-[3px]"
                    style={{ color: "var(--brand-text)", opacity: 0.38 }}
                  >
                    Affiliation
                  </p>
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0"
                    style={{ color: "var(--brand-text)" }}
                  >
                    {form.affiliation}
                  </p>
                </div>
                <div className="px-[20px] py-[14px]">
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.8px] uppercase m-0 mb-[3px]"
                    style={{ color: "var(--brand-text)", opacity: 0.38 }}
                  >
                    Sponsorship
                  </p>
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0"
                    style={{ color: "var(--brand-text)" }}
                  >
                    {form.hasSponsorship
                      ? `Yes — ${form.sponsorName}`
                      : "No external sponsorship"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          className="px-[24px] py-[20px] flex gap-[16px] shrink-0"
          style={{ borderTop: BORDER }}
        >
          {/* Left / Back */}
          {step >= 2 && step <= 3 && (
            <button
              onClick={() => setStep((s) => (s - 1) as Step)}
              className="flex-1 h-[48px] cursor-pointer bg-transparent font-['Helvetica_Neue:Medium',sans-serif] text-[16px]"
              style={{ border: "1px solid rgba(59,14,39,0.3)", color: "var(--brand-text)" }}
            >
              Back
            </button>
          )}

          {/* Right / Primary */}
          {step === 1 && (
            <button
              disabled={!step1Valid}
              onClick={() => setStep(2)}
              className="flex-1 h-[48px] border-none font-['Helvetica_Neue:Medium',sans-serif] text-[16px] transition-opacity"
              style={{
                backgroundColor: "var(--brand-primary-btn-bg)",
                color: "var(--brand-primary-btn-text)",
                opacity: step1Valid ? 1 : 0.3,
                cursor: step1Valid ? "pointer" : "not-allowed",
              }}
            >
              Next
            </button>
          )}
          {step === 2 && (
            <button
              disabled={!step2Valid}
              onClick={() => setStep(3)}
              className="flex-1 h-[48px] border-none font-['Helvetica_Neue:Medium',sans-serif] text-[16px] transition-opacity"
              style={{
                backgroundColor: "var(--brand-primary-btn-bg)",
                color: "var(--brand-primary-btn-text)",
                opacity: step2Valid ? 1 : 0.3,
                cursor: step2Valid ? "pointer" : "not-allowed",
              }}
            >
              Next
            </button>
          )}
          {step === 3 && (
            <button
              disabled={!step3Valid}
              onClick={handleSubmit}
              className="flex-1 h-[48px] border-none font-['Helvetica_Neue:Medium',sans-serif] text-[16px] transition-opacity"
              style={{
                backgroundColor: "var(--brand-primary-btn-bg)",
                color: "var(--brand-primary-btn-text)",
                opacity: step3Valid ? 1 : 0.3,
                cursor: step3Valid ? "pointer" : "not-allowed",
              }}
            >
              Register for the Challenge
            </button>
          )}
          {step === 4 && (
            <button
              onClick={onClose}
              className="flex-1 h-[48px] cursor-pointer border-none font-['Helvetica_Neue:Medium',sans-serif] text-[16px]"
              style={{
                backgroundColor: "var(--brand-primary-btn-bg)",
                color: "var(--brand-primary-btn-text)",
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}